import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  Edit3,
  Eye,
  Grid3X3,
  List,
  Pause,
  PlayCircle,
  Plus,
  RefreshCw,
  Trash2,
  TrendingUp,
  User,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import Pagination from '../Pagination';
import { Input } from '../forms';
import { TaskCard, TaskDetailsDrawer } from './';
import TaskDrawer from './TaskDrawer';
import TaskProgressDrawer from './TaskProgressDrawer';
import configService from '../../services/configService';
import { projectService } from '../../services/projectService';
import taskService from '../../services/taskService';
import {
  Project,
  Task,
  TaskDropdownOption,
  TaskPriority,
  TasksListParams,
  TasksListResponse,
  TaskStatistics,
  TaskStatus,
  TaskUser,
} from '../../types';

interface TaskListManagementProps {
  projectId?: string; // If provided, shows tasks for specific project
  title: string;
  description: string;
  showProjectSelector?: boolean; // Show project dropdown for global view
}

const TaskListManagement: React.FC<TaskListManagementProps> = ({
  projectId: initialProjectId,
  title,
  description,
  showProjectSelector = false,
}) => {
  const [tasksData, setTasksData] = useState<TasksListResponse | null>(null);
  const [statistics, setStatistics] = useState<TaskStatistics | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<TaskUser[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Config-driven dropdown options
  const [statusOptions, setStatusOptions] = useState<TaskDropdownOption[]>([]);
  const [priorityOptions, setPriorityOptions] = useState<TaskDropdownOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<TaskDropdownOption[]>([]);
  const [progressStatusOptions, setProgressStatusOptions] = useState<TaskDropdownOption[]>([]);

  // Search state for debouncing
  const [searchTerm, setSearchTerm] = useState('');

  // UI state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
  const [isTaskDetailsOpen, setIsTaskDetailsOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [isProgressDrawerOpen, setIsProgressDrawerOpen] = useState(false);
  const [progressTaskId, setProgressTaskId] = useState<string>('');

  // Project selection for global view
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    initialProjectId || (showProjectSelector ? 'all' : '')
  );

  // Filter and pagination state
  const [filters, setFilters] = useState<TasksListParams>({
    page: 1,
    limit: 12,
    status: 'all',
    priority: 'all',
    category: 'all',
    progressStatus: 'all',
    assignedToId: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  // Current project ID for API calls
  const currentProjectId = initialProjectId || selectedProjectId;
  const canShowTasks = !showProjectSelector || selectedProjectId !== 'all';

  // Load data
  useEffect(() => {
    if (canShowTasks && currentProjectId) {
      loadTasksData();
    } else if (showProjectSelector && selectedProjectId === 'all') {
      // Clear data when no project selected
      setTasksData({
        tasks: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      });
      setIsLoading(false);
    }
  }, [filters, selectedProjectId, canShowTasks, currentProjectId]);

  useEffect(() => {
    if (canShowTasks && currentProjectId) {
      loadStatistics();
      loadAssignableUsers();
    }
    if (showProjectSelector) {
      loadProjects();
    }
    loadConfigurations();
  }, [selectedProjectId, canShowTasks, currentProjectId, showProjectSelector]);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects({
        page: 1,
        limit: 1000,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      if (response.success && response.data) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Load dropdown configurations using generic config service
  const loadConfigurations = async () => {
    try {
      const config = await configService.getTaskConfigurations();

      setStatusOptions(config.statuses);
      setPriorityOptions(config.priorities);
      setCategoryOptions(config.categories);
      setProgressStatusOptions(config.progressStatuses);
    } catch (error) {
      console.error('Error loading task configurations:', error);
    }
  };

  const loadTasksData = async () => {
    if (!currentProjectId) return;

    setIsLoading(true);
    try {
      const response = await taskService.getTasks(currentProjectId, filters);
      if (response.success && response.data) {
        setTasksData(response.data);
      } else {
        setErrorMessage(response.message || 'Failed to load tasks');
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
      setErrorMessage('Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStatistics = async () => {
    if (!currentProjectId) return;

    setIsLoadingStats(true);
    try {
      const response = await taskService.getTaskStatistics(currentProjectId);
      if (response.success && response.data) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadAssignableUsers = async () => {
    if (!currentProjectId) return;

    try {
      const response = await taskService.getAssignableUsers(currentProjectId);
      if (response.success && response.data) {
        setAssignableUsers(response.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    (() => {
      let timeout: NodeJS.Timeout;
      return (searchValue: string) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          setFilters(prev => ({
            ...prev,
            search: searchValue,
            page: 1, // Reset to page 1 when search changes
          }));
        }, 300); // 300ms debounce
      };
    })(),
    []
  );

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (key: keyof TasksListParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value, // Reset to page 1 when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleStatusUpdate = async (taskId: string, status: TaskStatus) => {
    try {
      const response = await taskService.updateTask(taskId, { status });
      if (response.success) {
        setSuccessMessage('Task status updated successfully');
        await loadTasksData();
        await loadStatistics();
      } else {
        setErrorMessage(response.message || 'Failed to update task status');
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      setErrorMessage('Failed to update task status');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await taskService.deleteTask(taskId);
      if (response.success) {
        setSuccessMessage('Task deleted successfully');
        await loadTasksData();
        await loadStatistics();
      } else {
        setErrorMessage(response.message || 'Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      setErrorMessage('Failed to delete task');
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsTaskFormOpen(true);
  };

  const handleViewTask = (task: Task) => {
    setSelectedTaskId(task.id);
    setIsTaskDetailsOpen(true);
  };

  const handleTrackProgress = (task: Task) => {
    setProgressTaskId(task.id);
    setIsProgressDrawerOpen(true);
  };

  const handleTaskSaved = async () => {
    setIsTaskFormOpen(false);
    setEditingTask(undefined);
    await loadTasksData();
    await loadStatistics();
    setSuccessMessage(editingTask ? 'Task updated successfully' : 'Task created successfully');
  };

  const handleTaskUpdated = async () => {
    await loadTasksData();
    await loadStatistics();
  };

  const getStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <PlayCircle className="h-4 w-4 text-brand-primary" />;
      case 'on_hold':
        return <Pause className="h-4 w-4 text-yellow-600" />;
      case 'review':
        return <Clock className="h-4 w-4 text-purple-600" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-red-400';
      case 'medium':
        return 'bg-yellow-500';
      default:
        return 'bg-green-500';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const isOverdue = (dueDate: Date | undefined, status: TaskStatus) => {
    if (!dueDate || status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Success/Error Messages - Compact Toast Style */}
      {successMessage && (
        <div className="absolute top-2 right-4 z-50 bg-green-50 border border-green-200 rounded-md px-3 py-2 shadow-sm">
          <div className="flex items-center space-x-2">
            <span className="text-green-700 text-xs font-medium">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-500 hover:text-green-700 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="absolute top-2 right-4 z-50 bg-red-50 border border-red-200 rounded-md px-3 py-2 shadow-sm">
          <div className="flex items-center space-x-2">
            <span className="text-red-700 text-xs font-medium">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Compact Header Bar */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between">
            {/* Left: Title + Inline Stats */}
            <div className="flex items-center space-x-5">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                  <AlertTriangle className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">{title}</h1>
                  <p className="text-[11px] text-gray-500 leading-tight">{description}</p>
                </div>
              </div>

              {/* Inline Stats - Compact */}
              {statistics && (
                <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-gray-900">{statistics.total}</span>
                    <span className="text-[10px] text-gray-400">Total</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-green-600">
                      {statistics.completed}
                    </span>
                    <span className="text-[10px] text-gray-400">Done</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-purple-600">
                      {statistics.inProgress}
                    </span>
                    <span className="text-[10px] text-gray-400">Active</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-yellow-600">
                      {statistics.overdue}
                    </span>
                    <span className="text-[10px] text-gray-400">Overdue</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle - Compact */}
              <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Grid View"
                >
                  <Grid3X3 className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="List View"
                >
                  <List className="h-3.5 w-3.5" />
                </button>
              </div>

              <button
                onClick={() => {
                  loadTasksData();
                  loadStatistics();
                }}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>

              <button
                onClick={() => setIsTaskFormOpen(true)}
                disabled={!canShowTasks || !currentProjectId}
                className={`inline-flex items-center px-3 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
                  !canShowTasks || !currentProjectId
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-brand-600 text-white hover:bg-brand-700'
                }`}
              >
                <Plus className="h-3 w-3 mr-1" />
                New Task
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Compact Toolbar - Filters */}
      <div className="flex-none bg-white border-b border-gray-100 px-5 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Project Selector (if global view) */}
          {showProjectSelector && (
            <select
              value={selectedProjectId}
              onChange={e => setSelectedProjectId(e.target.value)}
              className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="all">Select project...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          )}

          {/* Search */}
          <div className="relative w-40">
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={e => handleSearchChange(e.target.value)}
              className="text-xs py-1.5 pl-7 pr-2"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={e => handleFilterChange('status', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">Status</option>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Priority Filter */}
          <select
            value={filters.priority}
            onChange={e => handleFilterChange('priority', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">Priority</option>
            {priorityOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={e => handleFilterChange('category', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">Category</option>
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Assignee Filter */}
          <select
            value={filters.assignedToId}
            onChange={e => handleFilterChange('assignedToId', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">Assignee</option>
            {assignableUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>

          <div className="h-4 w-px bg-gray-200" />

          {/* Sort */}
          <select
            value={filters.sortBy}
            onChange={e => handleFilterChange('sortBy', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="createdAt">Created</option>
            <option value="dueDate">Due Date</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="title">Title</option>
          </select>

          <select
            value={filters.sortOrder}
            onChange={e => handleFilterChange('sortOrder', e.target.value)}
            className="px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>

          {/* Task Count */}
          {tasksData && (
            <span className="text-[10px] text-gray-400 ml-auto">
              {tasksData.tasks.length} of {tasksData.totalCount}
            </span>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 p-4 bg-gray-50">
        <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          {!canShowTasks ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <AlertTriangle className="mx-auto h-8 w-8 text-gray-300" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Select a Project</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Choose a project from the dropdown to view tasks.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Tasks List */}
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <LoadingSpinner size="md" />
                </div>
              ) : tasksData?.tasks.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <AlertTriangle className="mx-auto h-8 w-8 text-gray-300" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No tasks found</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      {filters.search || filters.status !== 'all' || filters.priority !== 'all'
                        ? 'Try adjusting your filters.'
                        : 'Create your first task to get started.'}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Scrollable Content */}
                  <div className="flex-1 min-h-0 overflow-auto">
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
                        {tasksData?.tasks.map(task => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            onEdit={handleEditTask}
                            onDelete={handleDeleteTask}
                            onStatusChange={handleStatusUpdate}
                            onView={handleViewTask}
                            onTrackProgress={handleTrackProgress}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {tasksData?.tasks.map(task => (
                          <div
                            key={task.id}
                            className="px-4 py-2.5 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center">
                              <div
                                className={`w-1.5 h-1.5 rounded-full mr-3 flex-none ${getPriorityColor(task.priority)}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3">
                                  <h3 className="text-sm font-medium text-gray-900 truncate">
                                    {task.title}
                                  </h3>
                                  <div className="flex items-center space-x-1 text-[11px]">
                                    {getStatusIcon(task.status)}
                                    <span className="text-gray-500 capitalize">
                                      {task.status.replace('_', ' ')}
                                    </span>
                                  </div>
                                </div>
                                {task.description && (
                                  <p className="text-[11px] text-gray-500 truncate mt-0.5">
                                    {task.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center space-x-4 ml-4 text-[11px] text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <User className="h-3 w-3" />
                                  <span className="truncate max-w-[80px]">
                                    {task.assignedTo
                                      ? `${task.assignedTo.firstName}`
                                      : 'Unassigned'}
                                  </span>
                                </div>
                                {task.dueDate && (
                                  <div
                                    className={`flex items-center space-x-1 ${isOverdue(task.dueDate, task.status) ? 'text-red-600' : ''}`}
                                  >
                                    <Calendar className="h-3 w-3" />
                                    <span>{formatDate(task.dueDate)}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-0.5 ml-3">
                                <button
                                  onClick={() => handleViewTask(task)}
                                  className="p-1 text-gray-400 hover:text-brand-600 rounded transition-colors"
                                  title="View"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleTrackProgress(task)}
                                  className="p-1 text-gray-400 hover:text-green-600 rounded transition-colors"
                                  title="Progress"
                                >
                                  <TrendingUp className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleEditTask(task)}
                                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Edit3 className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTask(task.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pagination - Fixed at bottom */}
                  {tasksData && tasksData.totalPages > 1 && (
                    <div className="flex-none border-t border-gray-100 px-4 py-2 bg-gray-50">
                      <Pagination
                        currentPage={tasksData.currentPage}
                        totalPages={tasksData.totalPages}
                        totalItems={tasksData.totalCount}
                        itemsPerPage={filters.limit || 12}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Task Drawer */}
      {currentProjectId && (
        <TaskDrawer
          isOpen={isTaskFormOpen}
          onClose={() => {
            setIsTaskFormOpen(false);
            setEditingTask(undefined);
          }}
          onSave={handleTaskSaved}
          projectId={currentProjectId}
          task={editingTask}
          assignableUsers={assignableUsers}
        />
      )}

      {/* Task Details Drawer */}
      <TaskDetailsDrawer
        isOpen={isTaskDetailsOpen}
        onClose={() => {
          setIsTaskDetailsOpen(false);
          setSelectedTaskId('');
        }}
        taskId={selectedTaskId}
        onTaskUpdated={handleTaskUpdated}
      />

      {/* Task Progress Drawer */}
      <TaskProgressDrawer
        isOpen={isProgressDrawerOpen}
        onClose={() => {
          setIsProgressDrawerOpen(false);
          setProgressTaskId('');
        }}
        taskId={progressTaskId}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default TaskListManagement;
